import './NormalBidHistory.css';
import { useState, useEffect, useContext } from 'react';
import { WalletContext } from './WalletContext';

function StarlineWinningHistory({ onBack }) {
  const { walletBalance } = useContext(WalletContext);
  const [winnings, setWinnings] = useState([]);
  const [filteredWinnings, setFilteredWinnings] = useState([]);
  const [starlineGames, setStarlineGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [filters, setFilters] = useState({ gameName: '', gameType: '' });

  useEffect(() => {
    fetchStarlineGames();
    fetchWinningHistory();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [winnings, filters]);

  const fetchStarlineGames = async () => {
    try {
      const response = await fetch('https://backend.mainbazar777.site/api/fetch-starline-game');
      const data = await response.json();
      if (data.success) setStarlineGames(data.starline_games || []);
    } catch (err) {
      console.error('Error fetching starline games:', err);
    }
  };

  const fetchWinningHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const userId = user?.id;

      if (!userId) {
        setError('User ID not found. Please login again.');
        return;
      }

      const response = await fetch(
        `https://backend.mainbazar777.site/api/starline-win/${userId}`
      );
      const result = await response.json();

      if (result.success && result.starline_wins) {
        setWinnings(result.starline_wins);
      } else {
        setError('Failed to fetch starline winning history');
      }
    } catch (err) {
      setError('Error fetching winning history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = winnings;

    if (filters.gameName) {
      filtered = filtered.filter(item =>
        item.straline_game.game_name === filters.gameName
      );
    }

    if (filters.gameType) {
      filtered = filtered.filter(item =>
        item.straline_game_type.toLowerCase().includes(filters.gameType.toLowerCase())
      );
    }

    setFilteredWinnings(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => setFilters({ gameName: '', gameType: '' });

  const totalPages = Math.ceil(filteredWinnings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWinnings = filteredWinnings.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = new Set(
      [1, 2, currentPage - 1, currentPage, currentPage + 1, totalPages]
        .filter(p => p >= 1 && p <= totalPages)
    );
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
      result.push(sorted[i]);
    }
    return result;
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

  return (
    <div className="normal-bid-history-page">
      <header className="nbh-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <h1>Starline Winning History</h1>
      </header>

      <div className="nbh-content">
        {loading && <div className="loading">Loading starline winning history...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="filters-section">
              <h4>Filters</h4>
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Game Name</label>
                  <select
                    value={filters.gameName}
                    onChange={(e) => handleFilterChange('gameName', e.target.value)}
                  >
                    <option value="">All Games</option>
                    {starlineGames.map(game => (
                      <option key={game.id} value={game.game_name}>
                        {game.game_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Game Type</label>
                  <select
                    value={filters.gameType}
                    onChange={(e) => handleFilterChange('gameType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="Single Digit">Single Digit</option>
                    <option value="Single Panna">Single Panna</option>
                    <option value="Double Panna">Double Panna</option>
                    <option value="Triple Panna">Triple Panna</option>
                  </select>
                </div>
              </div>
              <button className="clear-filters-btn" onClick={clearFilters}>Clear Filters</button>
            </div>

            <div className="bid-history-list">
              <h4>Winning History ({filteredWinnings.length})</h4>

              {filteredWinnings.length === 0 ? (
                <div className="no-data">
                  {winnings.length === 0
                    ? 'No starline winning history found'
                    : 'No wins match the selected filters'}
                </div>
              ) : (
                <>
                  <div className="cards-container">
                    {paginatedWinnings.map((item, index) => (
                      <div key={index} className="bid-card">
                        <div className="bc-left">
                          <div className="bc-meta">
                            <span className="bc-game-name">{item.straline_game.game_name}</span>
                            <span className="bc-status status-won">WON</span>
                          </div>

                          <div className="bc-sub">
                            <span className="bc-game-type">{item.straline_game_type}</span>
                            {item.straline_game.open && (
                              <>
                                <span className="bc-dot">•</span>
                                <span className="bc-session-text">{item.straline_game.open}</span>
                              </>
                            )}
                          </div>

                          <div className="bc-bid">
                            <div className="bid-split">
                              <div className="bid-group">
                                <span className="bid-label">BID NO</span>
                                <span className="bid-part">{item.wins.bid_number}</span>
                              </div>
                              <span className="bid-arrow">→</span>
                              <div className="bid-group">
                                <span className="bid-label">BET AMT</span>
                                <span className="bid-part">₹{parseFloat(item.wins.bet_amount).toFixed(0)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bc-right">
                          <span className="bc-amount" style={{ color: '#2e7d32' }}>
                            ₹{parseFloat(item.wins.winning_amount).toFixed(2)}
                          </span>
                          <span className="bc-date">{formatDate(item.wins.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>

                      <div className="page-numbers">
                        {getPageNumbers().map((page, i) =>
                          page === '...' ? (
                            <span key={`e-${i}`} className="page-ellipsis">...</span>
                          ) : (
                            <button
                              key={page}
                              className={`page-number ${currentPage === page ? 'active' : ''}`}
                              onClick={() => goToPage(page)}
                            >
                              {page}
                            </button>
                          )
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}

                  <div className="pagination-info">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredWinnings.length)} of {filteredWinnings.length} wins
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default StarlineWinningHistory;
